import { RECOMMENDATION_LABELS, recommendationWord, type RecoTone } from "@/mc/labels";

// The words the debrief screens share — the facilitator's room, the interviewer's room and My Debriefs all read them
// from here so a rename lands everywhere at once (V1-INTERACTIONS §4.15).

export const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { created: "muted", scheduled: "info", revealed: "warn", decided: "ok", closed: "ok", cancelled: "muted" };
// AF-17 (EM-15): the column is "Stage", and Decided and Closed say what they mean — they were the only two states
// without the explanatory clause their neighbours carry, which is exactly the pair the client could not tell apart.
export const STATUS_LABEL: Record<string, string> = {
  created: "Created — to schedule",
  scheduled: "Scheduled",
  revealed: "In session — revealed",
  decided: "Decided — every outcome recorded",
  closed: "Closed — session ended",
  cancelled: "Cancelled",
};
//: the one line the screen carries beside the column, so the difference is legible without opening a record
export const STAGE_LEGEND = "Stage is how far the debrief has got. Decided means every candidate in the cycle has an outcome recorded; Closed means the session itself has ended and the sheets stay revealed in the room's record.";

// AF-19 (EM-17): Attendance takes three values — Required until the Recruiter takes the roll, then what actually
// happened. The server derives the word (`my_attendance_label`, `attendance_label`); these are only its tones, and the
// plain words for the invitation ANSWER, which is a different thing from attendance and is said separately.
export const ATTENDANCE_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { Required: "muted", Attended: "ok", "Not attended": "warn" };
export const ANSWER_SAID: Record<string, string> = { confirmed: "attending", declined: "cannot attend" };

// B14 (R5, C4, C5): the room's recommendation words come from the design system's one mapping (`recommendationWord`),
// and its choices from the server's offered set (`debrief.open.level.offered`, display order): Strong Hire · Hire ·
// Consider Other Role · No Hire on a playbook requisition, Hire · No Hire on a level-v1 one, the earlier scale's three
// on a legacy one. A read without the level block keeps the earlier scale's three.
export const V1_OFFERED: readonly string[] = ["hire", "hire_with_reservations", "no_hire"];

type Level = { offered?: unknown; hire_like?: unknown } | null | undefined;
const levelOf = (view: unknown): Level => {
  const raw = (view as { level?: unknown } | null | undefined)?.level;
  return raw && typeof raw === "object" ? (raw as Level) : null;
};
const strings = (raw: unknown): string[] => (Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : []);

/** The values a participant may align on, in the server's order. */
export function offeredOf(view: unknown): string[] {
  const offered = strings(levelOf(view)?.offered).filter((v) => recommendationWord(v) !== null);
  return offered.length ? offered : [...V1_OFFERED];
}

/** The values that count as Hire for the extend-offer gate (Strong Hire and Hire on a playbook requisition). */
export function hireLikeOf(view: unknown): string[] {
  const hireLike = strings(levelOf(view)?.hire_like);
  return hireLike.length ? hireLike : ["hire"];
}

/** A stored recommendation or alignment in his words, an earlier-scale value always with the earlier-scale marker
 * (DC-247); undefined for a value the mapping does not know. */
export function recoLabel(value: unknown): string | undefined {
  const word = typeof value === "string" && value ? recommendationWord(value) : null;
  if (!word) return undefined;
  return word.earlierScale ? `${word.label} · ${RECOMMENDATION_LABELS.earlierScale}` : word.label;
}

/** The V1 pill tone of a stored recommendation (DC-245: Consider Other Role a concern, the earlier scale gold). */
const V1_TONE: Readonly<Record<RecoTone, "ok" | "warn" | "bad">> = { "ok-strong": "ok", ok: "ok", concern: "warn", bad: "bad", wait: "warn" };
export function recoTone(value: unknown): "ok" | "warn" | "bad" | "muted" {
  const word = typeof value === "string" && value ? recommendationWord(value) : null;
  return word ? V1_TONE[word.tone] ?? "muted" : "muted";
}
